import {
  ChevronDownIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { FC, useRef, useState } from "react";
import type { SocialProviderType } from "../types/models";
import { toLocalDatetimeString } from "../utils/date";
import { providerTypeToIconLabel } from "./AddSocialProviderAccountButton";
import ConfirmAction from "./ConfirmAction";
import DatePicker from "./DatePicker";
import ScheduleDateLabel, {
  formatScheduleDateTimeLabel,
} from "./ScheduleDateLabel";
import Typography from "./Typography";

export type PublishCapacityRejection = {
  key: string;
  videoName: string;
  thumbnailUrl?: string;
  accountLabel: string;
  accountId: string;
  provider: SocialProviderType;
  requestedAt?: Date; // undefined = publish now
  suggestedAt?: Date; // undefined = no availability within the scan horizon
  // day_full / user_limit: YouTube's daily slots. account_limit: the posts a
  // platform takes from one account in 24 hours (TikTok 15).
  reason: "day_full" | "user_limit" | "account_limit";
};

type PublishCapacityDialogProps = {
  rejections: PublishCapacityRejection[];
  isPublishNowMode: boolean;
  userDailyLimit?: number;
  // Per provider, from the verify: posts one account can take in 24 hours.
  accountDailyLimits?: Partial<Record<string, number>>;
  // Maps rejection key -> chosen date. Rows without an entry are dropped.
  onConfirm: (pickedDates: Map<string, Date>) => void;
  onCancel: () => void;
};

const YOUTUBE_SLOTS_TOOLTIP =
  "YouTube limits how many posts apps like Klap can publish each day, so YouTube posts are booked in daily slots. We're working with YouTube to raise Klap's limit.";
const ACCOUNT_LIMIT_TOOLTIP =
  "Platforms refuse posts past a set number per account in 24 hours and can flag the account as spam, so Klap books posts within it.";

/** The prompt's wording for these rejections: named after their platform
 * when there is one, with the limit each hit. */
export function describeCapacityPrompt({
  rejections,
  isPublishNowMode,
  userDailyLimit,
  accountDailyLimits,
}: Pick<
  PublishCapacityDialogProps,
  "rejections" | "isPublishNowMode" | "userDailyLimit" | "accountDailyLimits"
>) {
  const providers = [...new Set(rejections.map((rejection) => rejection.provider))];
  const platform =
    providers.length === 1 ? providerTypeToIconLabel[providers[0]].label : null;
  const hasAccountLimit = rejections.some(
    (rejection) => rejection.reason === "account_limit",
  );
  const hasYoutubeSlots = rejections.some(
    (rejection) => rejection.reason !== "account_limit",
  );

  // YouTube capacity uses the reconnect prompt; per-account caps keep
  // this scheduling prompt.
  const titleText = hasAccountLimit
    ? platform
      ? `${platform} posting limit reached`
      : "Posting limits reached"
    : "YouTube posting limit reached";
  // YouTube's next slot is tomorrow; an account's is when its oldest post of
  // the last 24 hours frees a place, often sooner.
  const confirmLabel = !isPublishNowMode
    ? "Schedule with new dates"
    : hasAccountLimit
      ? "Schedule for later"
      : "Schedule for tomorrow";
  const limits = [
    ...(rejections.some((rejection) => rejection.reason === "user_limit") &&
    userDailyLimit
      ? [`You can schedule up to ${userDailyLimit} YouTube posts per day.`]
      : []),
    ...providers.flatMap((provider) => {
      const limit = accountDailyLimits?.[provider];
      return limit &&
        rejections.some(
          (rejection) =>
            rejection.provider === provider &&
            rejection.reason === "account_limit",
        )
        ? [`${providerTypeToIconLabel[provider].label} takes up to ${limit} posts per account in 24 hours.`]
        : [];
    }),
  ];
  const description = limits.length
    ? limits.join(" ")
    : "Here are the next available dates for the affected posts.";
  const tooltip = [
    ...(hasYoutubeSlots ? [YOUTUBE_SLOTS_TOOLTIP] : []),
    ...(hasAccountLimit ? [ACCOUNT_LIMIT_TOOLTIP] : []),
  ].join(" ");
  return { titleText, description, confirmLabel, tooltip };
}

// Shown when the pre-flight verify finds posts that don't fit: YouTube's
// daily publish capacity, or a platform's cap of posts per account in 24
// hours. One batch-level dialog: every affected post with its
// server-suggested replacement date (editable per row), accepted in a single
// click. Manual picks are validated by the re-verify on confirm.
const PublishCapacityDialog: FC<PublishCapacityDialogProps> = ({
  rejections,
  isPublishNowMode,
  userDailyLimit,
  accountDailyLimits,
  onConfirm,
  onCancel,
}) => {
  const [pickedDates, setPickedDates] = useState<Map<string, Date | undefined>>(
    () =>
      new Map(rejections.map((rejection) => [rejection.key, rejection.suggestedAt])),
  );
  const pickerRefs = useRef<Map<string, any>>(new Map());

  const { titleText, description, confirmLabel, tooltip } =
    describeCapacityPrompt({
      rejections,
      isPublishNowMode,
      userDailyLimit,
      accountDailyLimits,
    });

  const handlePick = (key: string, date: Date) => {
    setPickedDates((current) => new Map(current).set(key, date));
  };

  const openPicker = (key: string) => {
    const picker = pickerRefs.current.get(key);
    picker?.focus();
    picker?.showPicker();
  };

  const handleConfirm = () => {
    const confirmed = new Map<string, Date>();
    pickedDates.forEach((date, key) => {
      if (date) confirmed.set(key, date);
    });
    onConfirm(confirmed);
  };

  return (
    <ConfirmAction
      title={
        <span className="inline-flex items-center gap-1.5">
          {titleText}
          <InformationCircleIcon
            className="w-5 h-5 shrink-0 text-foreground-muted cursor-help"
            data-tooltip-id="global"
            data-tooltip-content={tooltip}
          />
        </span>
      }
      description={description}
      confirmLabel={confirmLabel}
      cancelLabel="Go back"
      onConfirm={handleConfirm}
      onCancel={onCancel}
    >
      <div className="flex flex-col w-full mt-4 divide-y divide-slate-150">
        {rejections.map((rejection) => {
          const picked = pickedDates.get(rejection.key);
          return (
            <div
              key={rejection.key}
              className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0 min-w-0"
            >
              {rejection.thumbnailUrl && (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={rejection.thumbnailUrl}
                  alt=""
                  className="w-8 h-12 rounded object-cover shrink-0 bg-slate-100"
                />
              )}
              <div className="flex flex-col flex-1 min-w-0">
                <Typography variant="subtitle3" className="truncate">
                  {rejection.videoName}
                </Typography>
                <Typography
                  variant="caption"
                  className="truncate text-foreground-secondary"
                >
                  {rejection.accountLabel}
                </Typography>
              </div>
              <div className="flex flex-col items-end shrink-0 text-right">
                {/* mr-4 offsets the trigger's chevron (size-3.5 + gap) so
                    both date texts right-align. */}
                {rejection.requestedAt && (
                  <Typography
                    variant="caption"
                    // Match the picker trigger's text-xs size.
                    className="line-through text-foreground-muted !text-xs mr-4"
                  >
                    {formatScheduleDateTimeLabel(rejection.requestedAt)}
                  </Typography>
                )}
                {!picked && !rejection.suggestedAt && (
                  <Typography
                    variant="caption"
                    className="text-foreground-muted !text-xs mr-4"
                  >
                    No availability in the next 30 days
                  </Typography>
                )}
                {/* Exact copy of the publishing dialog's per-post schedule
                    trigger (PublicationForm), plus `relative` to anchor the
                    hidden DatePicker overlay. */}
                <button
                  type="button"
                  onClick={() => openPicker(rejection.key)}
                  className="group relative inline-flex items-center gap-0.5 min-w-0 max-w-full mt-1"
                >
                  <ScheduleDateLabel
                    as="span"
                    className="min-w-0 max-w-full text-slate-500 group-hover:text-slate-700"
                    label={
                      picked
                        ? formatScheduleDateTimeLabel(picked)
                        : "Pick a date"
                    }
                  />
                  <ChevronDownIcon
                    className="size-3.5 shrink-0 text-slate-500 group-hover:text-slate-700"
                    aria-hidden
                  />
                  <DatePicker
                    className="absolute inset-0"
                    ref={(node: any) => {
                      if (node) pickerRefs.current.set(rejection.key, node);
                      else pickerRefs.current.delete(rejection.key);
                    }}
                    value={picked ? toLocalDatetimeString(picked) : undefined}
                    portalId="react-datepicker-popper-root"
                    popperStrategy="fixed"
                    popperPlacement="bottom-end"
                    onChange={(date) => handlePick(rejection.key, date)}
                  />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ConfirmAction>
  );
};

export default PublishCapacityDialog;
